/**
 * Paint Travel / Deadhead path + active segmentWindow on the 2D canvas
 * (matches amhs_4node_rectangle_player.html highlightSegmentWindows).
 *
 * Updates from simulationClockStore — not Redux simulationTime — so the
 * 10Hz scrubber sync does not rebuild these strokes / hitch the main stage.
 *
 * Perf: build highlights once per signature change (not twice). Above
 * HIGH_LEG_COUNT, only paint the active segmentWindow (skip full-path dim
 * strokes) so high FOUP counts stay smooth.
 */
import React, { useMemo, useSyncExternalStore } from 'react';
import { Group, Line } from 'react-konva';
import { useAppSelector } from '../../hooks/redux';
import {
  buildAllSegmentHighlights,
  buildAllSegmentHighlightsActiveOnly,
  type OhtSegmentHighlight,
} from '../../utils/simulationPhaseUi';
import { buildTrackGraph } from '../../utils/simulationGraph';
import { getExportableTrackEdges } from '../../utils/simulationTrackNetworkView';
import { ALL_RAIL_TYPES } from '../../utils/topology';
import { simulationClockStore } from '../../store/simulationClockStore';
import { liveMotionStore } from '../../store/liveMotionStore';
import type { AmhsFinalSimulationLeg } from '../../types/simulationPlayback';

/** Above this many apiLegs, drop dim full-path strokes (active window only). */
const HIGH_LEG_COUNT = 80;

/**
 * Highlight repaint rate, in wall milliseconds.
 *
 * The clock store notifies every animation frame, and the snapshot bucket is
 * keyed on simulation time — so at a 10x sim speed the bucket advanced every
 * frame and these strokes were rebuilt and reconciled at 60Hz. Path highlights
 * are context, not motion, so notifying at 10Hz is indistinguishable and keeps
 * the OHT layer's frame budget for the OHTs.
 */
const HIGHLIGHT_NOTIFY_INTERVAL_MS = 100;

function subscribeThrottledClock(onStoreChange: () => void): () => void {
  let lastNotifyMs = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const unsubscribe = simulationClockStore.subscribe(() => {
    const elapsed = performance.now() - lastNotifyMs;
    if (elapsed >= HIGHLIGHT_NOTIFY_INTERVAL_MS) {
      lastNotifyMs = performance.now();
      onStoreChange();
      return;
    }
    // Trailing edge, so the last frame of a move still lands.
    if (timer != null) return;
    timer = setTimeout(() => {
      timer = null;
      lastNotifyMs = performance.now();
      onStoreChange();
    }, HIGHLIGHT_NOTIFY_INTERVAL_MS - elapsed);
  });
  return () => {
    if (timer != null) clearTimeout(timer);
    unsubscribe();
  };
}

/** Avoid rebuilding highlights on every clock notify when the window set is unchanged. */
let cachedHighlightKey = '';
let cachedHighlightSig = '';
let cachedHighlights: OhtSegmentHighlight[] = [];

function highlightSnapshot(
  running: boolean,
  apiLegs: Parameters<typeof buildAllSegmentHighlights>[0],
  ohtColors: Record<string, string>
): string {
  if (!running) {
    cachedHighlights = [];
    return '';
  }
  // Prefer live motion store; fall back to the Redux mirror for legacy replay.
  const liveLegs = liveMotionStore.getApiLegs();
  const legs: AmhsFinalSimulationLeg[] =
    liveLegs.length > 0 ? liveLegs : (apiLegs as AmhsFinalSimulationLeg[]);
  const t = simulationClockStore.getTime();
  // Bucket so getSnapshot stays cheap between window changes.
  const key = `${Math.floor(t * 20)}|${legs.length}|${Object.keys(ohtColors).length}|${liveMotionStore.getRevision()}`;
  if (key === cachedHighlightKey) return cachedHighlightSig;
  cachedHighlightKey = key;
  const highlights =
    legs.length > HIGH_LEG_COUNT
      ? buildAllSegmentHighlightsActiveOnly(legs, t, ohtColors)
      : buildAllSegmentHighlights(legs, t, ohtColors);
  cachedHighlights = highlights;
  cachedHighlightSig = highlights
    .map(
      (h) =>
        `${h.ohtId}:${h.phase}:${h.activeSegmentId ?? ''}:${h.styles.map((s) => s.segmentId).join(',')}`
    )
    .join('|');
  return cachedHighlightSig;
}

const EMPTY_API_LEGS: any[] = [];

const SimulationSegmentHighlight: React.FC<{ zoomLevel?: number }> = ({ zoomLevel = 1 }) => {
  const running = useAppSelector((state) => state.simulation.running);
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);
  const source = useAppSelector((state) => state.simulation.source);
  const legacyApiLegs = useAppSelector((state) =>
    state.simulation.source === 'live'
      ? EMPTY_API_LEGS
      : (state.simulation.browserKinetic?.apiLegs ?? EMPTY_API_LEGS)
  );
  const elements = useAppSelector((state) => state.layout.elements);
  const liveFleetSignature = useSyncExternalStore(
    (onStoreChange) => liveMotionStore.subscribe(onStoreChange),
    () => (source === 'live' ? liveMotionStore.getFleetSignature() : ''),
    () => ''
  );

  const layoutOhtColors = useMemo(() => {
    const map: Record<string, string> = {};
    for (const el of Object.values(elements)) {
      if (el.type !== 'oht-vehicle') continue;
      const ohtId =
        (typeof el.customProperties?.ohtId === 'string' && el.customProperties.ohtId) ||
        el.id;
      map[ohtId] = el.metadata.color || '#31C8C8';
      map[el.id] = map[ohtId];
    }
    return map;
  }, [elements]);

  // Live fleet signature only changes when the OHT set changes.
  const streamedOhtIds = liveFleetSignature;

  const ohtColors = useMemo(() => {
    const map: Record<string, string> = { ...layoutOhtColors };
    for (const ohtId of streamedOhtIds ? streamedOhtIds.split('|') : []) {
      if (!map[ohtId]) map[ohtId] = '#31C8C8';
    }
    return map;
  }, [layoutOhtColors, streamedOhtIds]);

  const pointsBySegmentId = useMemo(() => {
    const layoutList = Object.values(elements).filter(
      (el) => ALL_RAIL_TYPES.has(el.type) && el.metadata.visible
    );
    const graph = buildTrackGraph(layoutList);
    const exportable = getExportableTrackEdges(graph, layoutList);
    const map = new Map<string, number[]>();
    for (const { edge, segmentId } of exportable) {
      const pts = edge.points;
      if (!pts || pts.length < 2) continue;
      const flat: number[] = [];
      for (const p of pts) {
        flat.push(p.x, p.y);
      }
      map.set(segmentId, flat);
    }
    return map;
  }, [elements]);

  // Re-render only when the active window / path set changes (not every clock tick).
  const signature = useSyncExternalStore(
    subscribeThrottledClock,
    () => highlightSnapshot(running, legacyApiLegs, ohtColors),
    () => ''
  );

  const highlights = useMemo(() => {
    if (!running || !signature) return [];
    // Reuse the build from highlightSnapshot — do not rebuild.
    return cachedHighlights;
    // signature encodes time-dependent highlight state
     
  }, [running, signature]);

  if (!running || view3dOpen || highlights.length === 0) return null;

  const strokes = highlights
    .flatMap((h) => h.styles)
    .sort((a, b) => Number(a.active) - Number(b.active));

  return (
    <Group listening={false}>
      {strokes.map((style, index) => {
        const points = pointsBySegmentId.get(style.segmentId);
        if (!points || points.length < 4) return null;
        return (
          <Line
            key={`${style.segmentId}-${style.active ? 'a' : 'p'}-${index}`}
            points={points}
            stroke={style.stroke}
            strokeWidth={style.width / zoomLevel}
            opacity={style.opacity}
            dash={style.dash?.map((d) => d / zoomLevel)}
            lineCap="round"
            lineJoin="round"
            perfectDrawEnabled={false}
            listening={false}
            shadowForStrokeEnabled={false}
            hitStrokeWidth={0}
          />
        );
      })}
    </Group>
  );
};

export default React.memo(SimulationSegmentHighlight);
